/**
 * Creates a simple deferred promise
 */
class Deferred {
	constructor() {
		this.promise = new Promise((resolve, reject) => {
			this.resolve = resolve;
			this.reject = reject;
		});
	}
}

/**
 * A simple abstraction of sending RPC messages across an event bus and receiving responses.
 * This is intended to be used to communicate with the native side of the application.
 * Users of this class are expected to tell this how to post messages and tell it when a message was received.
 * The means in which messages are sent and received are not defined here and should be implemented by the user.
 */
class EventRPCBridge {
	/** @type {Object<string, Deferred>} */
	_callbackDefermentMap = {};
	/**
	 * @type {function({method: string, callbackId: string, payload: any}): void}
	 */
	_postMessage;

	/**
	 * A unique key to "namespace" our messages.
	 * Right now this is put in just to be sure that if there are 2 bridges on the same bus the messages don't get mixed up.
	 * It's not actually needed as of this moment, more of a safety rail for the future.
	 * @type {string}
	 * @private
	 */
	_RPCKey = Math.random().toString(36).substring(2);
	_messageId = 0;

	/**
	 *
	 * @param {{
	 *     postMessage: function({method: string, callbackId: string, payload: any}): void
	 * }} options
	 */
	constructor(
		options
	) {
		this._postMessage = options.postMessage;
	}

	/**
	 * Invoke the native method with the given arguments. Arguments should be expcted to be serialized to JSON
	 * @param {string} method
	 * @param {Object} payload
	 * @return {Promise<unknown>}
	 */
	async invokeRPCMethod(method, payload) {
		const callbackId = this._createCallbackId();
		// register our deferment on our callbacks to be called later
		const deferment = new Deferred();
		this._callbackDefermentMap[callbackId] = deferment;

		// send the message to the native side
		this._postMessage({
			method,
			callbackId,
			payload,
		});

		return deferment.promise;
	}

	/**
	 * Resolves or rejects the original caller's promise. Should be called when a method complete message is received from the other side of the bridge.
	 *
	 * @param {{
	 *    callbackId: string,
	 *    result?: any,
	 *    error?: any,
	 *    message?: string,
	 * }} message
	 */
	onRPCMethodComplete(message) {
		if(!message) {
			console.error('EventRPCBridge Received invalid message: message is falsy', message);
			return;
		}
		if(!message.callbackId) {
			console.error('EventRPCBridge Received invalid message: no callbackId', message);
			return;
		}

		const deferment = this._callbackDefermentMap[message.callbackId];
		if(!deferment) {
			console.error('EventRPCBridge Received invalid message: callbackId is not in our deferment map, unresolvable', message);
			return;
		}

		if (message.error) {
			deferment.reject(new Error(message.message || message.error));
		} else {
			deferment.resolve(message.result);
		}
		delete this._callbackDefermentMap[message.callbackId];
	}

	/**
	 * Creates a callback ID which is guaranteed to be unique in this context
	 *
	 * @return {string}
	 * @private
	 */
	_createCallbackId() {
		if(Object.keys(this._callbackDefermentMap).length > 20) {
			console.warn('There are a lot of outstanding RPC calls. This may be indicative of a problem');
		}

		this._messageId++;
		return `${this._RPCKey}-${this._messageId}`;
	}
}

module.exports = {
	EventRPCBridge
}